import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { NaturalLanguageSearchSuggestionResource } from "api/resource/natural-language-search-suggestion";
import { NaturalLanguageSearchSuggestion, NewNaturalLanguageSearchSuggestion } from "model/natural-language-search-suggestion";
import { arrayToById, objectToById } from "redux/normalize";
import { loginReset } from "./authentication";

export interface NaturalLanguageSearchSuggestionsState {
	loading?: boolean;
	error?: string | null;
	byId: {
		[key: number]: NaturalLanguageSearchSuggestionResource;
	};
}

const initialState: NaturalLanguageSearchSuggestionsState = {
	byId: {},
};

// Async thunks
const fetchNaturalLanguageSearchSuggestions = createAsyncThunk(
	"naturalLanguageSearchSuggestions/fetch",
	async (payload, thunkAPI) => {
		thunkAPI.dispatch(naturalLanguageSearchSuggestionsLoading());
		const result = api.getNaturalLanguageSearchSuggestions();
		result
			.then((suggestions) => thunkAPI.dispatch(naturalLanguageSearchSuggestionsLoaded(suggestions)))
			.catch(error => thunkAPI.dispatch(naturalLanguageSearchSuggestionsFailed(error)));
		return result;
	}
);

const createNaturalLanguageSearchSuggestion = createAsyncThunk(
	"naturalLanguageSearchSuggestions/create",
	async (payload: { suggestion: NewNaturalLanguageSearchSuggestion }, thunkAPI) => {
		thunkAPI.dispatch(naturalLanguageSearchSuggestionsLoading());
		const result = api.createNaturalLanguageSearchSuggestion(payload.suggestion);
		result
			.then(suggestion => thunkAPI.dispatch(naturalLanguageSearchSuggestionLoaded(suggestion)))
			.catch(error => thunkAPI.dispatch(naturalLanguageSearchSuggestionsFailed(error)));
		return result;
	}
);

const updateNaturalLanguageSearchSuggestion = createAsyncThunk(
	"naturalLanguageSearchSuggestions/update",
	async (payload: { suggestion: NaturalLanguageSearchSuggestion }, thunkAPI) => {
		thunkAPI.dispatch(naturalLanguageSearchSuggestionsLoading());
		const result = api.updateNaturalLanguageSearchSuggestion(payload.suggestion);
		result
			.then(suggestion => thunkAPI.dispatch(naturalLanguageSearchSuggestionLoaded(suggestion)))
			.catch(error => thunkAPI.dispatch(naturalLanguageSearchSuggestionsFailed(error)));
		return result;
	}
);

const deleteNaturalLanguageSearchSuggestion = createAsyncThunk(
	"naturalLanguageSearchSuggestions/delete",
	async (payload: { id: number }, thunkAPI) => {
		thunkAPI.dispatch(naturalLanguageSearchSuggestionsLoading());
		const result = api.deleteNaturalLanguageSearchSuggestion(payload.id);
		result
			.then(() => thunkAPI.dispatch(naturalLanguageSearchSuggestionDeleted(payload.id)))
			.catch(error => thunkAPI.dispatch(naturalLanguageSearchSuggestionsFailed(error)));
		return result;
	}
);

// Slice
export const naturalLanguageSearchSuggestionsSlice = createSlice({
	name: "naturalLanguageSearchSuggestions",
	initialState,
	reducers: {
		naturalLanguageSearchSuggestionsLoading: (state) => {
			state.loading = true;
		},
		naturalLanguageSearchSuggestionsLoaded: (state, action: PayloadAction<NaturalLanguageSearchSuggestionResource[]>) => {
			state.loading = false;
			state.error = null;
			state.byId = arrayToById(action.payload, "id");
		},
		naturalLanguageSearchSuggestionLoaded: (state, action: PayloadAction<NaturalLanguageSearchSuggestionResource>) => {
			const byId = objectToById(action.payload, "id");
			state.loading = false;
			state.error = null;
			state.byId = { ...state.byId, ...byId };
		},
		naturalLanguageSearchSuggestionDeleted: (state, action: PayloadAction<number>) => {
			state.loading = false;
			state.error = null;
			const { [action.payload]: deleted, ...remaining } = state.byId;
			state.byId = remaining;
		},
		naturalLanguageSearchSuggestionsFailed: (state, action: PayloadAction<any>) => {
			state.loading = false;
			state.error = action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	naturalLanguageSearchSuggestionsLoading,
	naturalLanguageSearchSuggestionsLoaded,
	naturalLanguageSearchSuggestionLoaded,
	naturalLanguageSearchSuggestionDeleted,
	naturalLanguageSearchSuggestionsFailed,
} = naturalLanguageSearchSuggestionsSlice.actions;

export {
	fetchNaturalLanguageSearchSuggestions,
	createNaturalLanguageSearchSuggestion,
	updateNaturalLanguageSearchSuggestion,
	deleteNaturalLanguageSearchSuggestion,
};
